import React from "react";

import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { Row, StackProps } from "@rui/Stack";

export interface ChartSeriesLabelProps {
  active: boolean;
  color: React.CSSProperties["color"];
  disabled?: boolean;
  label: string;
  score?: number;
  onClick?: () => void;
}

type LabelWrapperProps = StackProps &
  Pick<ChartSeriesLabelProps, "active" | "disabled" | "onClick">;

const LabelWrapper = styled(
  (props: LabelWrapperProps) => <Row variant="center" spacing={1} {...props} />,
  { shouldForwardProp: (prop) => prop !== "active" }
)(({ active, disabled, theme }) => ({
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  borderRadius: "4px",
  height: "28px",
  width: "fit-content",
  padding: "4px 8px",
  transition:
    "background-color 0.1s ease-in-out, border-color 0.1s ease-in-out",
  "&:hover": {
    borderColor: disabled
      ? theme.palette.shades.neutral[100]
      : theme.palette.shades.primary[500],
  },
  backgroundColor: active ? "#FFF" : theme.palette.shades.neutral[50],
  cursor: disabled ? "default" : "pointer",
}));

type LegendProps = Pick<ChartSeriesLabelProps, "active" | "color">;

const Legend = styled((props: LegendProps) => <Box {...props} />, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "color",
})(({ active, color, theme }) => ({
  borderRadius: "50%",
  width: "8px",
  height: "8px",
  border: "3px solid",
  borderColor: active ? color : theme.palette.shades.neutral[300],
  transition: "border-color 0.1s ease-in-out",
}));

const ChartSeriesLabel = ({
  active,
  color,
  disabled,
  label,
  onClick,
  score,
}: ChartSeriesLabelProps) => (
  <LabelWrapper active={active} disabled={disabled} onClick={onClick}>
    <Typography variant="bodySecondaryMed">{label}</Typography>
    <Legend active={active} color={color} />
    {score != null && (
      <Typography variant="bodySecondaryMed">{score}</Typography>
    )}
  </LabelWrapper>
);

export default ChartSeriesLabel;
